import { Injectable } from '@angular/core';

import { C } from '../@shared/constants';
import { ProtoObject, ProtoReturnValue } from 'src/app/@core/types';
import { User } from '@common/entities';

type BooleanStorageKey = 'isTrackingEnabled' | 'appTrackingTransparencyAllowed' | 'hasAcceptedTerms';
type NumberStorageKey = 'appStarts';
type StringStorageKey = 'accessToken' | 'firstVisit' | 'language' | 'lastVisit' | 'appVersion' | 'lastRatingPopupDate';
type UserStorageKey = 'user';
type ArrayStorageKey = 'lastConnectedDevices';
type StorageKey = BooleanStorageKey | NumberStorageKey | UserStorageKey | StringStorageKey | ArrayStorageKey;

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private storage: Storage;

  constructor() {
    this.storage = localStorage;
  }

  /**
   * Use for storing values, stringifies values
   */

  public set(key: BooleanStorageKey, value: boolean): void;
  public set(key: NumberStorageKey, value: number): void;
  public set(key: StringStorageKey, value: string): void;
  public set(key: UserStorageKey, value: ProtoObject): void;
  public set<T>(key: string, value: T): void;
  public set(key: StorageKey, value: ProtoReturnValue): void;
  public set(key: StorageKey, value: ProtoReturnValue): void {
    switch (typeof value) {
      case 'function':
        throw new Error('Yeah, so, functions cannot be saved into localStorage.');

      case 'string':

      // eslint-disable-next-line no-fallthrough
      case 'number':

      // eslint-disable-next-line no-fallthrough
      case 'boolean':
        this.storage.setItem(C.STORAGE_PREFIX + key, value.toString());
        break;

      case 'undefined':
        this.storage.setItem(C.STORAGE_PREFIX + key, '');
        break;

      case 'object':
        // case objects, array and null
        this.storage.setItem(C.STORAGE_PREFIX + key, JSON.stringify(value));
        break;

      default:
        throw new Error('Unknown type!');
    }
  }

  public get(key: BooleanStorageKey, defaultValue?: boolean): boolean;
  public get(key: NumberStorageKey, defaultValue?: number): number;
  public get(key: StringStorageKey, value: string): string;
  public get(key: UserStorageKey, Value?: ProtoObject): User;
  public get(key: StorageKey, defaultValue?: ProtoReturnValue): ProtoReturnValue;
  public get<T, U>(key: string, defaultValue?: U): T;
  public get(key: StorageKey, defaultValue?: ProtoReturnValue): ProtoReturnValue {
    let item = this.storage.getItem(C.STORAGE_PREFIX + key) || defaultValue;

    // We do not need to parse the default value
    if (item !== defaultValue) {
      item = this.parseJsonIfNeeded(item as string);
    }

    return item;
  }

  public remove(key: StorageKey): void {
    this.storage.removeItem(C.STORAGE_PREFIX + key);
  }

  public clear(): void {
    for (const item in this.storage) {
      if (!this.storage.hasOwnProperty(item)) { continue; }

      // eslint-disable-next-line @typescript-eslint/prefer-includes
      if (item.indexOf(C.STORAGE_PREFIX) >= 0) {
        this.storage.removeItem(item);
      }
    }
  }

  public getAllItems(): ProtoObject {
    const items = {
      ...this.storage,
    };

    for (const item in items) {
      if (!items.hasOwnProperty(item)) { continue; }

      items[item] = this.parseJsonIfNeeded(items[item]);

      // eslint-disable-next-line @typescript-eslint/prefer-includes
      if (item.indexOf(C.STORAGE_PREFIX) < 0) {
        delete items[item];
      }
    }

    return items;
  }

  private parseJsonIfNeeded(item: string): ProtoReturnValue {
    let parsedItem: ProtoReturnValue;

    // console.info(item);
    try {
      parsedItem = JSON.parse(item || '{}'); // TODO Why an empty object, does that make sense?
    } catch (e) { // eslint-disable-line @typescript-eslint/no-unused-vars
      // Item is probably a string like 'Hello world' which cannot be parsed,
      // no hard feelings, just don't let the app crash

      return item;
    }

    return parsedItem;
  }
}
